import type { ChangeEvent, FocusEvent, KeyboardEvent } from 'react';

import { Info, Minus, Plus } from 'lucide-react';
import { useCallback, useState } from 'react';

import type { VadParameterDefinition } from './vad-config';

import { clampVadValue, formatVadValue } from './vad-config';

interface VadParameterFieldProps {
  readonly definition: VadParameterDefinition;
  readonly value: number;
  readonly onChange: (value: number) => void;
  readonly disabled?: boolean;
}

/**
 * Touch-first parameter control: 44px steppers for fine adjustments, a tappable
 * numeric field for direct input, and a slider whose two ends are labelled with
 * their concrete effect — no units, no jargon.
 */
export function VadParameterField({
  definition,
  value,
  onChange,
  disabled = false,
}: VadParameterFieldProps): React.JSX.Element {
  const [showInfo, setShowInfo] = useState<boolean>(false);
  const labelId = `vad-label-${definition.key}`;

  const commit = useCallback(
    (raw: number): void => {
      onChange(clampVadValue(definition, raw));
    },
    [definition, onChange],
  );

  const stepBy = (direction: 1 | -1): void => {
    commit(value + direction * definition.step);
  };

  const handleSliderChange = (event: ChangeEvent<HTMLInputElement>): void => {
    commit(Number(event.target.value));
  };

  const commitTextInput = (event: FocusEvent<HTMLInputElement>): void => {
    const parsed = Number(event.currentTarget.value.replace(',', '.'));
    if (Number.isFinite(parsed)) {
      commit(parsed);
    } else {
      event.currentTarget.value = formatVadValue(definition, value);
    }
  };

  const handleTextKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.currentTarget.blur();
    }
  };

  const formattedValue = formatVadValue(definition, value);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1">
        <span id={labelId} className="text-sm font-medium text-foreground">
          {definition.label}
        </span>
        <button
          type="button"
          onClick={() => setShowInfo(current => !current)}
          aria-expanded={showInfo}
          aria-label={`Description du paramètre ${definition.label}`}
          className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted"
        >
          <Info className="h-4 w-4" />
        </button>
      </div>

      {showInfo && (
        <p className="rounded-lg bg-muted/50 p-3 text-xs leading-relaxed text-muted-foreground">
          {definition.description}
        </p>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => stepBy(-1)}
          disabled={disabled || value <= definition.min}
          aria-label={`Diminuer ${definition.label}`}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border bg-background transition-colors hover:bg-muted disabled:opacity-40"
        >
          <Minus className="h-5 w-5" />
        </button>

        <div className="flex min-w-0 flex-1 flex-col">
          <input
            type="range"
            aria-labelledby={labelId}
            min={definition.min}
            max={definition.max}
            step={definition.step}
            value={value}
            disabled={disabled}
            onChange={handleSliderChange}
            className="h-9 w-full cursor-pointer appearance-none bg-transparent [&::-moz-range-thumb]:h-6 [&::-moz-range-thumb]:w-6 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-primary [&::-moz-range-track]:h-2 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:bg-muted [&::-webkit-slider-runnable-track]:h-2 [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-runnable-track]:bg-muted [&::-webkit-slider-thumb]:-mt-2 [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary"
          />
          <div className="flex justify-between gap-2" aria-hidden="true">
            <span className="text-[11px] leading-tight text-muted-foreground">
              {definition.sliderMinLabel}
            </span>
            <span className="text-right text-[11px] leading-tight text-muted-foreground">
              {definition.sliderMaxLabel}
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={() => stepBy(1)}
          disabled={disabled || value >= definition.max}
          aria-label={`Augmenter ${definition.label}`}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border bg-background transition-colors hover:bg-muted disabled:opacity-40"
        >
          <Plus className="h-5 w-5" />
        </button>

        <div className="flex h-11 shrink-0 items-center rounded-lg border bg-background focus-within:ring-2 focus-within:ring-ring">
          <input
            key={formattedValue}
            type="text"
            inputMode="decimal"
            aria-label={`Valeur de ${definition.label}`}
            defaultValue={formattedValue}
            onBlur={commitTextInput}
            onKeyDown={handleTextKeyDown}
            disabled={disabled}
            className="h-full w-16 bg-transparent text-center text-sm tabular-nums outline-none"
          />
          {definition.unit && (
            <span className="pr-2 text-xs text-muted-foreground">{definition.unit}</span>
          )}
        </div>
      </div>
    </div>
  );
}
